/**
 * Chiamate del wizard (API-CONTRACT §3) e forma delle risposte.
 * I tipi degli enum vengono dai contratti; qui si descrivono solo i corpi delle rotte.
 */
import type { Sensitivity, WizardAnswerKind, WizardSessionStatus } from '@mamma/contracts'

import { api } from './client'

export type CardOption = { id: string; label_it: string }

export type WizardCardData = {
  id: string
  block: string
  block_title_it: string
  progress_it: string
  purpose_it: string
  question_it: string
  answer_kinds: WizardAnswerKind[]
  options: CardOption[]
  multi: boolean
  free_text: boolean
  field_label_it: string | null
  examples_it: string[] | null
  sensitivity: Sensitivity
  consent_scope: string[]
  save_kind: string
  confirm_required: boolean
  escapes: { kind: WizardAnswerKind; label_it: string }[]
  ask_agent_label_it: string
  note_it: string | null
}

export type SessionData = {
  id: string
  status: WizardSessionStatus
  current_block: string
  current_block_title_it: string
  current_question_id: string | null
  progress_it: string | null
}

export type SessionView = {
  session: SessionData
  /** DEC-15: unica fonte del nome dell'app. */
  display_form: string | null
  recap_it: string | null
  resume_it: string | null
  next_question: WizardCardData | null
}

export type ProposedFact = {
  id: string
  domain: string
  predicate: string
  value_text: string
  certainty: string
  sensitivity: Sensitivity
  needs_confirm: boolean
}

export type AnswerResult = {
  answer_id: string
  session: SessionData
  display_form: string | null
  proposed_facts: ProposedFact[]
  conflicts: string[]
  module_signals: { module: string; strength: number }[]
  next_question: WizardCardData | null
}

export type SummaryItemData = {
  fact_id: string
  value_text: string
  certainty: string
  sensitivity: Sensitivity
  needs_confirm: boolean
  actions: string[]
}

export type SummaryData = {
  session: SessionData
  sections: { domain: string; title_it: string; items: SummaryItemData[] }[]
  preferences: {
    question_id: string
    block_title_it: string
    label_it: string
    value_it: string | null
    answer_kind: WizardAnswerKind
  }[]
  medications: { id: string; declared_name: string; dose_text_it: string; prn: boolean }[]
  pending_health_fact_ids: string[]
  can_complete: boolean
  /** DEC-21: `accepted` = «Sì» a «Facciamo subito una cosa utile?»; `opening_it` apre la chat. */
  first_action: { module: string | null; text_it: string; accepted?: boolean; opening_it?: string }
}

export type AnswerBody = {
  question_id: string
  answer_kind: WizardAnswerKind
  raw_text?: string
  option?: string
  options?: string[]
}

/**
 * Una sola `GET /wizard/session` in volo alla volta: all'avvio la chiedono App, l'intro e la
 * schermata, e al primissimo avvio la sessione viene creata (indice unico sulla sessione attiva).
 */
let sessionInFlight: Promise<SessionView> | null = null

function session(): Promise<SessionView> {
  sessionInFlight ??= api.get<SessionView>('/wizard/session').finally(() => {
    sessionInFlight = null
  })
  return sessionInFlight
}

export const wizardApi = {
  session,
  answer: (body: AnswerBody) => api.post<AnswerResult>('/wizard/answers', body),
  pause: () => api.post<{ session: SessionData }>('/wizard/pause'),
  resume: () => api.post<SessionView>('/wizard/resume'),
  summary: () => api.get<SummaryData>('/wizard/summary'),
  complete: () => api.post<{ session: SessionData }>('/wizard/complete'),
  confirmFact: (factId: string, confirmedText: string) =>
    api.post(`/facts/${factId}/confirm`, { confirmed_text: confirmedText }),
  retireFact: (factId: string) => api.post(`/facts/${factId}/retire`, {}),
  /** "Chiedi a mia …": passa da POST /chat/turns quando la lane A lo implementa. */
  askAgent: (text: string, opening?: string) =>
    api.post<{ reply_text?: string }>('/chat/turns', opening ? { text, opening } : { text }),
}
